import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import unsplash from '../api/unsplash';
import GlobalSnackbar from './Snackbar';
import PasswordEditor, { getPasswordRequirements } from './PasswordEditor';
import PasswordPageShell from './PasswordPageShell';

const SetPassword = () => {
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [activationStatus, setActivationStatus] = useState('loading');
    const [activationUsername, setActivationUsername] = useState('');
    const [openSnack, setOpenSnack] = useState(false);
    const [snack, setSnack] = useState({
        severity: 'error',
        messaggio: '',
    });

    const token = useMemo(
        () => new URLSearchParams(window.location.search).get('token'),
        []
    );

    useEffect(() => {
        const verifyToken = async () => {
            if (!token) {
                setActivationStatus('invalid');
                return;
            }

            try {
                const response = await unsplash.get('utenti/activation', {
                    params: { token },
                });
                if (response.data.valid) {
                    setActivationUsername(response.data.username || '');
                    setActivationStatus('valid');
                } else {
                    setActivationStatus('invalid');
                }
            } catch {
                setActivationStatus('invalid');
            }
        };

        verifyToken();
    }, [token]);

    const passwordIsValid = getPasswordRequirements(password).every(
        ({ valid }) => valid
    );
    const passwordsMatch =
        confirmPassword.length > 0 && password === confirmPassword;

    const showError = (message) => {
        setSnack({ severity: 'error', messaggio: message });
        setOpenSnack(true);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (activationStatus !== 'valid' || !token) {
            showError('Link non valido, scaduto o già utilizzato');
            return;
        }
        if (!passwordIsValid) {
            showError('La password non rispetta tutti i requisiti');
            return;
        }
        if (!passwordsMatch) {
            showError('Le password inserite non coincidono');
            return;
        }

        setSubmitting(true);
        try {
            const response = await unsplash.put('utenti', {
                token,
                password,
                confirmPassword,
                operazione: 'OnlyPassword',
            });

            if (response.data.message === 'utente updated successfully') {
                navigate('/login');
                return;
            }
            showError(
                response.data.message || 'Errore durante il cambio password'
            );
        } catch (error) {
            showError(
                error.response?.data?.message ||
                    'Errore durante il cambio password'
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PasswordPageShell
            icon={<LockOutlined />}
            title="Imposta la password"
            subtitle="Completa l'attivazione del tuo account"
        >
            <GlobalSnackbar
                severity={snack.severity}
                messaggio={snack.messaggio}
                openSnack={openSnack}
                handleCloseSnack={(event, reason) => {
                    if (reason !== 'clickaway') setOpenSnack(false);
                }}
            />
            {activationUsername && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 2,
                        mb: 2.5,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                        color: 'text.secondary',
                    }}
                >
                    Account: <strong>{activationUsername}</strong>
                </Typography>
            )}

            {activationStatus === 'loading' ? (
                <Box
                    sx={{
                        minHeight: 180,
                        display: 'grid',
                        placeItems: 'center',
                    }}
                >
                    <Stack spacing={1.5} alignItems="center">
                        <CircularProgress size={32} />
                        <Typography variant="body2" color="text.secondary">
                            Verifica del link in corso...
                        </Typography>
                    </Stack>
                </Box>
            ) : activationStatus === 'invalid' ? (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    Questo link non è valido, è scaduto oppure è già stato
                    utilizzato.
                </Alert>
            ) : (
                <PasswordEditor
                    password={password}
                    setPassword={setPassword}
                    confirmPassword={confirmPassword}
                    setConfirmPassword={setConfirmPassword}
                    onSubmit={handleSubmit}
                    submitting={submitting}
                    submitLabel="Imposta password"
                />
            )}
        </PasswordPageShell>
    );
};

export default SetPassword;
